import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { primitiveTypeUniq } from '@mews/fp';
import { NewRoutingRule, NewRoutingRuleResult, Service, useRepositoryQuery } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { lt, useFeatureFlag } from '@mews/framework';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { Banner } from '@optimus-web/core';
import { getChildrenProductCategoryIdsFromProductCategoryId } from '@mews-commander/legacy-entities/src/utils/productCategories';
import { AddProductFormValues, ServiceOrderFormField } from '../AddProduct/schema';
import { useBillingAutomationOwnerName } from './useBillingAutomationOwnerName';
import { useAppliedRoutingRulesIds } from './useAppliedRoutingRulesIds';

const NOT_PRODUCTS_MOVED_RESULT: ProductsMovedByRoutingRuleResult = {
    banner: null,
    allProducts: false,
    productsIds: [],
    routingRule: null,
};

export interface ProductsMovedByRoutingRuleResult {
    banner: React.ReactNode;
    allProducts: boolean;
    productsIds: string[];
    routingRule?: NewRoutingRule | null;
}

export const useProductsMovedByRoutingRule = (
    accountId: string,
    service: Service,
    isVirtualBill: boolean,
    routingRuleResult?: NewRoutingRuleResult
): ProductsMovedByRoutingRuleResult => {
    const enterpriseId = useCurrentEnterpriseId();
    const newRoutingRulesPOSEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesPOSEnabled);
    const { watch } = useFormContext<AddProductFormValues>();
    const linkedReservationId = watch(ServiceOrderFormField.LinkedReservationId);

    const appliedRoutingRulesIds = useAppliedRoutingRulesIds({
        service,
        accountId,
        isVirtualBill,
        reservationIds: linkedReservationId ? [linkedReservationId] : undefined,
    });

    const { data: productCategoriesResult } = useRepositoryQuery(
        'productCategories',
        'getAll',
        {
            enterpriseIds: [enterpriseId],
        },
        { enabled: newRoutingRulesPOSEnabled && isVirtualBill }
    );

    const { data: productsResult } = useRepositoryQuery(
        'products',
        'getAll',
        {
            enterpriseIds: [enterpriseId],
        },
        { enabled: newRoutingRulesPOSEnabled && isVirtualBill }
    );

    const appliedRoutingRuleId = appliedRoutingRulesIds?.[0];
    const routingRule = routingRuleResult?.routingRules?.find((rule) => rule.id === appliedRoutingRuleId);

    const ownedBy = useBillingAutomationOwnerName(accountId, routingRule);

    return useMemo(() => {
        if (!newRoutingRulesPOSEnabled || !linkedReservationId || !appliedRoutingRuleId || !routingRuleResult) {
            return NOT_PRODUCTS_MOVED_RESULT;
        }

        const routingRuleAssignments =
            routingRuleResult.routingRuleAssignments?.filter(
                (assignment) => assignment.routingRuleId === appliedRoutingRuleId && assignment.serviceId === service.id
            ) ?? [];

        if (routingRuleAssignments.length) {
            const routingRuleAssignment = routingRuleAssignments[0];
            const productAssignments =
                routingRuleResult.productAssignments?.filter(
                    (assignment) => assignment.routingRuleAssignmentId === routingRuleAssignment.id
                ) ?? [];
            const productCategoryAssignments =
                routingRuleResult.productCategoryAssignments?.filter(
                    (assignment) => assignment.routingRuleAssignmentId === routingRuleAssignment.id
                ) ?? [];

            // Product category ids from assignments and their children categories
            const productCategoryIds = pipe(
                productCategoryAssignments,
                array.flatMap((productCategoryAssignment) => [
                    productCategoryAssignment.productCategoryId,
                    ...getChildrenProductCategoryIdsFromProductCategoryId(
                        productCategoryAssignment.productCategoryId,
                        productCategoriesResult?.productCategories ?? []
                    ),
                ]),
                primitiveTypeUniq
            );

            const productsFromProductCategories = (productsResult?.products || []).filter(
                (product) => product.productCategoryId && productCategoryIds.includes(product.productCategoryId)
            );

            const accountingCategoryAssignments =
                routingRuleResult.accountingCategoryAssignments?.filter(
                    (assignment) => assignment.routingRuleAssignmentId === routingRuleAssignment.id
                ) ?? [];

            const accountingCategoryAssignmentIds = pipe(
                accountingCategoryAssignments,
                array.map((assignment) => assignment.accountingCategoryId),
                primitiveTypeUniq
            );

            const productsFromAccountingCategories = (productsResult?.products || []).filter(
                (product) =>
                    product.accountingCategoryId &&
                    accountingCategoryAssignmentIds.includes(product.accountingCategoryId)
            );

            const productsIds = [
                ...productAssignments.map((assignment) => assignment.productId),
                ...productsFromProductCategories.map((product) => product.id),
                ...productsFromAccountingCategories.map((product) => product.id),
            ];

            const productsMoved = productsIds.length || routingRuleAssignment.routedItemTypes.allProducts;

            if (productsMoved) {
                return {
                    banner: (
                        <Banner
                            primaryText={lt('AddServiceToReservationProductsMovedByBillingAutomationBanner', {
                                ownedBy,
                                billingAutomation: routingRule?.name ?? '',
                            })}
                            variant="info"
                        />
                    ),
                    allProducts: routingRuleAssignment.routedItemTypes.allProducts,
                    productsIds,
                    routingRule,
                };
            }
        }

        return NOT_PRODUCTS_MOVED_RESULT;
    }, [
        newRoutingRulesPOSEnabled,
        ownedBy,
        productCategoriesResult?.productCategories,
        productsResult?.products,
        routingRule,
        service.id,
        linkedReservationId,
        appliedRoutingRuleId,
        routingRuleResult,
    ]);
};
